import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext.hook';
import { useToast } from '@/hooks/use-toast';

export type AdminNotifCategory =
  | 'importante'
  | 'atualizacao'
  | 'documento'
  | 'novo_investimento';

export interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: 'approval' | 'rejection' | 'info' | 'warning' | 'success' | 'communication';
  read: boolean;
  metadata?: Record<string, unknown>;
  created_at: string;
  read_at?: string;
  // Campos adicionados para broadcasts admin-driven (NULL em notificações legadas)
  category?: AdminNotifCategory | null;
  dispatch_id?: string | null;
  url?: string | null;
  investment_id?: string | null;
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const handleError = useCallback((err: unknown, context: string) => {
    const message = err instanceof Error ? err.message : 'Ocorreu um erro desconhecido';
    setError(message);
    console.error(`Error ${context}:`, err);
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setNotifications(data || []);
      setUnreadCount((data || []).filter(n => !n.read).length);
    } catch (err: unknown) {
      handleError(err, 'fetching notifications');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, handleError]);

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user?.id) return false;

    try {
      const { error: updateError } = await supabase
        .from('notifications')
        .update({
          read: true,
          read_at: new Date().toISOString()
        })
        .eq('id', notificationId)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId
            ? { ...n, read: true, read_at: new Date().toISOString() }
            : n
        )
      );

      setUnreadCount(prev => Math.max(0, prev - 1));
      return true;
    } catch (err: unknown) {
      handleError(err, 'marking notification as read');
      return false;
    }
  }, [user?.id, handleError]);

  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return false;

    try {
      const { error: updateError } = await supabase
        .from('notifications')
        .update({
          read: true,
          read_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .eq('read', false);

      if (updateError) throw updateError;

      setNotifications(prev =>
        prev.map(n => ({
          ...n,
          read: true,
          read_at: n.read_at || new Date().toISOString()
        }))
      );

      setUnreadCount(0);
      return true;
    } catch (err: unknown) {
      handleError(err, 'marking all notifications as read');
      return false;
    }
  }, [user?.id, handleError]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!user?.id) return false;

    try {
      const { error: deleteError } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      const notificationToDelete = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      if (notificationToDelete && !notificationToDelete.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      return true;
    } catch (err: unknown) {
      handleError(err, 'deleting notification');
      return false;
    }
  }, [user?.id, notifications, handleError]);

  const createNotification = useCallback(async (
    userId: string,
    title: string,
    message: string,
    type: Notification['type'] = 'info',
    metadata?: Record<string, unknown>
  ) => {
    try {
      const { data, error: rpcError } = await supabase
        .rpc('create_notification', {
          p_user_id: userId,
          p_title: title,
          p_message: message,
          p_type: type,
          p_metadata: metadata ? JSON.stringify(metadata) : null
        });

      if (rpcError) throw rpcError;
      return data;
    } catch (err: unknown) {
      handleError(err, 'creating notification');
      return null;
    }
  }, [handleError]);

  const showToastNotification = useCallback((notification: Notification) => {
    const getVariant = (n: Notification): 'default' | 'destructive' => {
      // Broadcasts admin-driven: categoria importante = destrutivo
      if (n.category === 'importante') return 'destructive';
      switch (n.type) {
        case 'warning':
        case 'rejection':
          return 'destructive';
        default:
          return 'default';
      }
    };

    toast({
      title: notification.title,
      description: notification.message,
      variant: getVariant(notification),
      duration: 5000,
    });
  }, [toast]);

  // Ref para manter o callback do toast fora das deps do useEffect abaixo.
  // shadcn's `toast` não é referencialmente estável, então incluir
  // showToastNotification nas deps disparava re-execução do effect a cada
  // render: o cleanup async de removeChannel não terminava antes do próximo
  // .channel(mesmo topic).on().subscribe(), e o Supabase Realtime rejeitava
  // com "cannot add postgres_changes callbacks ... after subscribe()".
  const showToastRef = useRef(showToastNotification);
  useEffect(() => {
    showToastRef.current = showToastNotification;
  }, [showToastNotification]);

  useEffect(() => {
    if (!user?.id) return;

    fetchNotifications();

    // Topic único por instância: NotificationDropdown é montado em vários
    // layouts (Base, Mobile, InvestorDashboard, AssessorPortfolio), e o
    // Supabase JS deduplica channels pelo nome — a 2ª instância caía num
    // channel já subscrito, disparando "cannot add postgres_changes
    // callbacks ... after subscribe()" e derrubando toda a árvore via
    // ErrorBoundary. Sufixo aleatório dá a cada instância seu próprio
    // canal. (Trade-off: N conexões WS em vez de 1; ideal seria virar
    // Context com uma única subscription.)
    const channelId = crypto.randomUUID();
    const notificationSubscription = supabase
      .channel(`notifications:${user.id}:${channelId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotification = payload.new as Notification;

          setNotifications(prev => {
            // Evita duplicatas caso fetch e INSERT realtime cheguem juntos
            if (prev.some(n => n.id === newNotification.id)) return prev;
            return [newNotification, ...prev];
          });
          setUnreadCount(prev => prev + 1);

          showToastRef.current(newNotification);
        }
      )
      .subscribe();

    // Em PWA mobile o WebSocket é encerrado quando o app vai para background.
    // Ao retornar para foreground o realtime reconecta mas não replay eventos
    // perdidos → refetch manual garante que o sino mostre notificações que
    // chegaram com app fechado.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // Também resyncroniza ao voltar de offline → online
    window.addEventListener('online', fetchNotifications);
    // E quando a janela recupera foco (desktop/tab switch)
    window.addEventListener('focus', fetchNotifications);

    return () => {
      supabase.removeChannel(notificationSubscription);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', fetchNotifications);
      window.removeEventListener('focus', fetchNotifications);
    };
  }, [user?.id, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    loading: isLoading, // Alias para retrocompatibilidade
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    createNotification,
  };
};
